import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { SubjectService } from '@app/services/subject.service';
import { BuyerintQrService } from '@app/services/buyerint-qr.service';
import { UsersService } from '@app/services/users.service';
import { ModalService } from '@services/modal.service';
import { HelperService } from '@services/helper.service';
import { Branch } from '@models/branch.model';

@Component({
  selector: 'app-qr-payment',
  templateUrl: './qr-payment.component.html',
  styleUrls: ['./qr-payment.component.scss']
})
export class QrPaymentComponent implements OnInit, OnDestroy {
  branches: Branch[] = [];
  branchSelected: Branch | null = null;
  generatedForBranch: Branch | null = null;
  imageSrc: SafeUrl | null = null;

  private qrBlobUrl: string | null = null;
  private printFrame: HTMLIFrameElement | null = null;
  private qrImageSubscription?: Subscription;
  private qrPdfSubscription?: Subscription;

  get qrAlreadyGeneratedForSelection(): boolean {
    return !!this.branchSelected && this.branchSelected === this.generatedForBranch;
  }

  constructor(
    public subject: SubjectService,
    public buyerintQr: BuyerintQrService,
    public userService: UsersService,
    public modal: ModalService,
    private spinner: NgxSpinnerService,
    private sanitizer: DomSanitizer,
    private helper: HelperService,
  ) { }

  ngOnInit(): void {
    this.subject.headerCollapse(true);
    this.getBranches();
  }

  ngOnDestroy(): void {
    this.subject.headerCollapse(false);
    this.cancelPendingRequests();
    this.resetQrState();
    this.removePrintFrame();
  }

  getBranches(): void {
    this.spinner.show();
    const user = this.userService.getIdentity();

    this.buyerintQr.getBranches(user.userInfo.documentType, user.userInfo.document).subscribe({
      next: (data) => {
        this.spinner.hide();
        this.branches = data.data;
      },
      error: () => {
        this.spinner.hide();
        this.showError('¡No hemos podido cargar las sucursales!');
      }
    });
  }

  onBranchSelectedChange(branch: Branch | null): void {
    this.branchSelected = branch;

    if (branch === this.generatedForBranch) {
      return;
    }

    this.cancelPendingRequests();
    this.spinner.hide();
    this.resetQrState();
  }

  onGenerateQrImage(branch: Branch): void {
    this.spinner.show();
    this.branchSelected = branch;
    this.unsubscribe(this.qrImageSubscription);
    this.resetQrState();

    this.qrImageSubscription = this.buyerintQr
      .getQrCodeImage(String(branch.documentType), String(branch.document), String(branch.queryCode))
      .subscribe({
        next: (data) => {
          this.spinner.hide();
          this.qrBlobUrl = this.createObjectUrl(data, 'image/png');
          this.imageSrc = this.sanitizer.bypassSecurityTrustUrl(this.qrBlobUrl);
          this.generatedForBranch = branch;
        },
        error: () => {
          this.spinner.hide();
          this.imageSrc = null;
          this.showError('¡No hemos podido generar el código QR!');
        }
      });
  }

  onGenerateQrPrintPdf(): void {
    if (!this.generatedForBranch) {
      return;
    }

    const branch = this.generatedForBranch;
    this.spinner.show();
    this.unsubscribe(this.qrPdfSubscription);

    this.qrPdfSubscription = this.buyerintQr
      .getQrCodePdf(String(branch.documentType), String(branch.document), String(branch.queryCode))
      .subscribe({
        next: (resp) => {
          this.spinner.hide();
          const pdfUrl = this.createObjectUrl(this.helper.base64ToBinary(resp.data.base64), 'application/pdf');
          this.printPdf(pdfUrl);
        },
        error: () => {
          this.spinner.hide();
          this.showError('¡No hemos podido generar el código QR para imprimir!');
        }
      });
  }

  private resetQrState(): void {
    if (this.qrBlobUrl) {
      URL.revokeObjectURL(this.qrBlobUrl);
    }

    this.imageSrc = null;
    this.qrBlobUrl = null;
    this.generatedForBranch = null;
  }

  private cancelPendingRequests(): void {
    this.unsubscribe(this.qrImageSubscription);
    this.unsubscribe(this.qrPdfSubscription);
    this.qrImageSubscription = undefined;
    this.qrPdfSubscription = undefined;
  }

  private unsubscribe(subscription?: Subscription): void {
    if (subscription) {
      subscription.unsubscribe();
    }
  }

  private createObjectUrl(data: BlobPart, type: string): string {
    return URL.createObjectURL(new Blob([data], { type }));
  }

  private showError(title: string): void {
    this.modal.show('qrError', { title });
  }

  private printPdf(pdfUrl: string): void {
    this.removePrintFrame();

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    this.printFrame = iframe;

    iframe.onload = () => {
      if (iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }
    };
  }

  private removePrintFrame(): void {
    if (!this.printFrame) {
      return;
    }

    const pdfUrl = this.printFrame.src;
    this.printFrame.remove();
    this.printFrame = null;
    URL.revokeObjectURL(pdfUrl);
  }
}
